import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  CalendarRange,
  ChevronDown,
  Clock3,
  CreditCard,
  Facebook,
  FileCheck2,
  Gift,
  Globe2,
  HelpCircle,
  Image as ImageIcon,
  Info,
  Instagram,
  Linkedin,
  Mail,
  Menu,
  MessageSquareText,
  Minus,
  MonitorCog,
  Plus,
  RotateCcw,
  Settings2,
  Shapes,
  ShieldCheck,
  Sparkles,
  Tags,
  Twitter,
  UserRound,
  X,
  Youtube
} from 'lucide-react';
import ThemeToggle from '../ThemeToggle';
import StocktagBrandLogo from '../StocktagBrandLogo.jsx';
import { Accordion, Dialog } from 'radix-ui';
import { LANGUAGE_OPTIONS } from '../../locales';
import { openCookieSettings } from '../../services/cookiePreferences';
import { localizeInternalHref, localizeSeoHref } from '../../utils/localeRouting';
import { SITE_SOCIAL_LINKS } from '../../utils/seoDiscovery';
import {
  DEFAULT_ZOOM_LEVEL,
  MAX_ZOOM_LEVEL,
  MIN_ZOOM_LEVEL,
  normalizeZoomLevel
} from '../../utils/zoomLevel.js';
import {
  getMetadataStudioThemeAssets,
  METADATA_STUDIO_THEMES
} from '../../data/metadataStudioAssets.js';
import {
  PUBLIC_MARKETING_ANCHORS,
  PUBLIC_MARKETING_ROUTES
} from '../../data/publicMarketingNavigation.js';
import { PUBLIC_MARKETING_HEADER_COPY_BN } from '../../data/i18n/publicChrome.bn.js';
import { PUBLIC_MARKETING_HEADER_COPY_HI } from '../../data/i18n/publicChrome.hi.js';
import { PUBLIC_MARKETING_HEADER_COPY_PT } from '../../data/i18n/publicChrome.pt.js';
import '../../styles/PublicMarketingShell.css';

const REFERRAL_CREDITS = 50;

const resolveReferralCreditsCopy = (value = '') => (
  String(value || '').replace('%%CREDITS%%', String(REFERRAL_CREDITS))
);

const SHELL_COPY_BY_LANGUAGE = {
  en: {
    product: 'Product', solutions: 'Solutions', tools: 'Tools', resources: 'Resources', platformExports: 'Platform exports',
    metadataTools: 'Metadata tools', display: 'Display', displayPreferences: 'Display settings',
    zoom: 'Zoom', theme: 'Theme', switchTheme: 'Switch theme', studio: 'Open Studio', menu: 'Menu', closeMenu: 'Close menu',
    navigation: 'Main menu', navigationDescription: 'Explore Stocktag products, tools, resources, and account settings.',
    account: 'Account', legal: 'Legal', social: 'Social profiles', about: 'About',
    editorial: 'Editorial policy', contact: 'Contact', privacy: 'Privacy notice', terms: 'Terms of service'
  },
  tr: {
    product: 'Ürün', solutions: 'Çözümler', tools: 'Araçlar', resources: 'Kaynaklar', platformExports: 'Platform dışa aktarmaları',
    metadataTools: 'Meta veri araçları', display: 'Görünüm', displayPreferences: 'Görünüm ayarları',
    zoom: 'Yakınlaştırma', theme: 'Tema', switchTheme: 'Temayı değiştir', studio: 'Stüdyoyu Aç', menu: 'Menü', closeMenu: 'Menüyü kapat',
    navigation: 'Ana menü', navigationDescription: 'Stocktag ürünlerine, araçlarına, kaynaklarına ve hesap ayarlarına göz atın.',
    account: 'Hesap', legal: 'Yasal', social: 'Sosyal profiller', about: 'Hakkımızda',
    editorial: 'Editoryal politika', contact: 'İletişim', privacy: 'Gizlilik bildirimi', terms: 'Kullanım koşulları'
  },
  es: {
    product: 'Producto', solutions: 'Soluciones', tools: 'Herramientas', resources: 'Recursos', platformExports: 'Exportaciones por plataforma',
    metadataTools: 'Herramientas de metadatos', display: 'Aspecto', displayPreferences: 'Preferencias de visualización',
    zoom: 'Zoom', theme: 'Tema', switchTheme: 'Cambiar de tema', studio: 'Abrir Studio', menu: 'Menú', closeMenu: 'Cerrar menú',
    navigation: 'Menú principal', navigationDescription: 'Explora los productos, las herramientas y los recursos de Stocktag, o gestiona tu cuenta.',
    account: 'Cuenta', legal: 'Información legal', social: 'Redes sociales', about: 'Sobre Stocktag',
    editorial: 'Política editorial', contact: 'Contacto', privacy: 'Política de privacidad', terms: 'Condiciones de servicio'
  },
  fr: {
    product: 'Produit', solutions: 'Solutions', tools: 'Outils', resources: 'Ressources', platformExports: 'Exports par place de marché',
    metadataTools: 'Outils de métadonnées', display: 'Affichage', displayPreferences: 'Préférences d’affichage',
    zoom: 'Zoom', theme: 'Thème', switchTheme: 'Changer de thème', studio: 'Ouvrir le Studio', menu: 'Menu', closeMenu: 'Fermer le menu',
    navigation: 'Menu principal', navigationDescription: 'Découvrez les produits, les outils et les ressources de Stocktag, ou gérez votre compte.',
    account: 'Compte', legal: 'Informations légales', social: 'Réseaux sociaux', about: 'À propos de Stocktag',
    editorial: 'Politique éditoriale', contact: 'Contact', privacy: 'Avis de confidentialité', terms: 'Conditions d’utilisation'
  },
  de: {
    product: 'Produkt', solutions: 'Lösungen', tools: 'Tools', resources: 'Ratgeber', platformExports: 'Exporte für Marktplätze',
    metadataTools: 'Metadaten-Tools', display: 'Darstellung', displayPreferences: 'Darstellung anpassen',
    zoom: 'Zoom', theme: 'Farbschema', switchTheme: 'Farbschema wechseln', studio: 'Metadata Studio öffnen', menu: 'Menü', closeMenu: 'Menü schließen',
    navigation: 'Hauptnavigation', navigationDescription: 'Entdecken Sie die Produkte, Tools und Ratgeber von Stocktag oder verwalten Sie Ihr Konto.',
    account: 'Konto', legal: 'Rechtliches', social: 'Social-Media-Kanäle', about: 'Über Stocktag',
    editorial: 'Redaktionsrichtlinie', contact: 'Kontakt', privacy: 'Datenschutzhinweis', terms: 'Nutzungsbedingungen'
  },
  it: {
    product: 'Prodotto', solutions: 'Soluzioni', tools: 'Strumenti', resources: 'Risorse', platformExports: 'Esportazioni per piattaforma',
    metadataTools: 'Strumenti per i metadati', display: 'Aspetto', displayPreferences: 'Preferenze di visualizzazione',
    zoom: 'Zoom', theme: 'Tema', switchTheme: 'Cambia tema', studio: 'Apri lo Studio', menu: 'Menu', closeMenu: 'Chiudi il menu',
    navigation: 'Navigazione', navigationDescription: 'Esplora i prodotti, gli strumenti, le risorse e le impostazioni dell’account Stocktag.',
    account: 'Account', legal: 'Informazioni legali', social: 'Profili social', about: 'Chi siamo',
    editorial: 'Politica editoriale', contact: 'Contatti', privacy: 'Informativa sulla privacy', terms: 'Termini di servizio'
  },
  id: {
    product: 'Produk', solutions: 'Solusi', tools: 'Alat', resources: 'Referensi', platformExports: 'Ekspor per platform',
    metadataTools: 'Alat metadata', display: 'Tampilan', displayPreferences: 'Pengaturan tampilan',
    zoom: 'Zoom', theme: 'Tema', switchTheme: 'Ganti tema', studio: 'Buka Studio', menu: 'Menu', closeMenu: 'Tutup menu',
    navigation: 'Menu utama', navigationDescription: 'Jelajahi produk, alat, referensi, dan pengaturan akun Stocktag.',
    account: 'Akun', legal: 'Informasi hukum', social: 'Media sosial', about: 'Tentang Stocktag',
    editorial: 'Kebijakan editorial', contact: 'Kontak', privacy: 'Pemberitahuan privasi', terms: 'Ketentuan layanan'
  },
  th: {
    product: 'ผลิตภัณฑ์', solutions: 'โซลูชัน', tools: 'เครื่องมือ', resources: 'คู่มือและแหล่งข้อมูล', platformExports: 'ส่งออกตามแพลตฟอร์ม',
    metadataTools: 'เครื่องมือเมทาดาทา', display: 'หน้าจอ', displayPreferences: 'ตั้งค่าหน้าจอ',
    zoom: 'ซูม', theme: 'โหมดสี', switchTheme: 'เปลี่ยนโหมดสี', studio: 'เปิดสตูดิโอ', menu: 'เมนู', closeMenu: 'ปิดเมนู',
    navigation: 'เมนูหลัก', navigationDescription: 'เลือกดูผลิตภัณฑ์ เครื่องมือ คู่มือ และการตั้งค่าบัญชีของ Stocktag',
    account: 'บัญชี', legal: 'ข้อมูลทางกฎหมาย', social: 'ช่องทางโซเชียล', about: 'เกี่ยวกับเรา',
    editorial: 'นโยบายบรรณาธิการ', contact: 'ติดต่อ', privacy: 'ประกาศความเป็นส่วนตัว', terms: 'ข้อกำหนดการใช้บริการ'
  }
};
Object.assign(SHELL_COPY_BY_LANGUAGE, {
  bn: PUBLIC_MARKETING_HEADER_COPY_BN,
  hi: PUBLIC_MARKETING_HEADER_COPY_HI,
  pt: PUBLIC_MARKETING_HEADER_COPY_PT
});

const SOCIAL_ICON_BY_ID = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  x: Twitter,
  youtube: Youtube
};

const isPlainPrimaryClick = (event) => (
  event.button === 0
  && !event.metaKey
  && !event.ctrlKey
  && !event.shiftKey
  && !event.altKey
);

const ActionLink = ({ href, onAction, onNavigate, children, ...props }) => {
  if (!href) {
    return (
      <button
        type="button"
        {...props}
        onClick={(event) => {
          onNavigate?.();
          onAction?.(event);
        }}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      {...props}
      onClick={(event) => {
        if (!isPlainPrimaryClick(event)) return;
        onNavigate?.();
        if (!onAction) return;
        event.preventDefault();
        onAction(event);
      }}
    >
      {children}
    </a>
  );
};

const STUDIO_PREVIEW_THEMES = new Set(METADATA_STUDIO_THEMES);

const StudioMegaPreview = ({ theme, title, body, actionLabel, href, onAction, onNavigate }) => {
  const previewTheme = STUDIO_PREVIEW_THEMES.has(theme) ? theme : 'light';
  const previewAsset = useMemo(
    () => getMetadataStudioThemeAssets(previewTheme).megaMenu,
    [previewTheme]
  );
  const imageCandidates = useMemo(() => [previewAsset.desktopSrc], [previewAsset]);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => setImageIndex(0), [previewTheme]);

  return (
    <ActionLink
      className="pm-studio-preview"
      href={href}
      onAction={onAction}
      onNavigate={onNavigate}
      aria-label={`${title}: ${actionLabel}`}
    >
      <div className="pm-studio-preview-media" aria-hidden="true">
        {imageIndex < imageCandidates.length ? (
          <img
            src={imageCandidates[imageIndex]}
            srcSet={previewAsset.srcSet}
            sizes="(max-width: 1180px) 42vw, 420px"
            alt=""
            width={previewAsset.width}
            height={previewAsset.height}
            loading="lazy"
            decoding="async"
            onError={() => setImageIndex((currentIndex) => currentIndex + 1)}
          />
        ) : (
          <div className="pm-studio-preview-fallback">
            <span className="pm-preview-sidebar" />
            <span className="pm-preview-toolbar" />
            <span className="pm-preview-card pm-preview-card-one" />
            <span className="pm-preview-card pm-preview-card-two" />
            <span className="pm-preview-keywords" />
          </div>
        )}
        <span className="pm-studio-preview-shine" />
      </div>
      <span className="pm-studio-preview-copy">
        <span>
          <strong>{title}</strong>
          <small>{body}</small>
        </span>
        <span className="pm-studio-preview-action">
          {actionLabel}
          <ArrowRight size={14} aria-hidden="true" />
        </span>
      </span>
    </ActionLink>
  );
};

const MegaPanelVisual = ({ group, theme, language, studioHref, onOpenStudio, onNavigate }) => {
  if (group.visual === 'studio') {
    return (
      <StudioMegaPreview
        theme={theme}
        title={group.previewTitle}
        body={group.previewBody}
        actionLabel={group.previewAction}
        href={studioHref}
        onAction={onOpenStudio}
        onNavigate={onNavigate}
      />
    );
  }

  const visualItems = (group.items || []).slice(0, 3);
  return (
    <div className={`pm-mega-coded-visual pm-mega-coded-visual--${group.visual || 'links'}`} aria-hidden="true">
      <div className="pm-mega-coded-heading">
        <span><Sparkles size={14} />{group.previewTitle || group.label}</span>
        <small>{group.previewBody || group.description}</small>
      </div>
      <div className="pm-mega-coded-items">
        {visualItems.map((item, index) => {
          const Icon = item.icon || ArrowRight;
          return (
            <div key={item.id || item.href} style={{ '--pm-visual-index': index }}>
              <span><Icon size={16} /></span>
              <strong>{item.label}</strong>
              {group.visual === 'workflow' && index < visualItems.length - 1 ? <ArrowRight size={13} /> : null}
            </div>
          );
        })}
      </div>
      <span className="pm-mega-coded-locale">{String(language || 'en').toUpperCase()}</span>
    </div>
  );
};

const getNavigationGroupId = (group, groupIndex) => {
  const fallbackId = `group-${groupIndex + 1}`;
  const safeId = String(group.id || fallbackId).trim().replace(/[^a-zA-Z0-9_-]+/g, '-');
  return safeId || fallbackId;
};

const PublicMarketingHeader = ({
  language = 'en',
  setLanguage,
  texts,
  theme = 'light',
  toggleTheme,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  isAuthenticated = false,
  authLoading = false,
  accountLabel = '',
  homeHref = '/',
  studioHref = '/metadata',
  loginHref = '/metadata?auth=login',
  accountHref = '/metadata',
  historyHref = '/metadata?view=history',
  pricingHref = '/pricing',
  tourHref = '/metadata',
  onOpenStudio,
  onOpenLogin,
  onOpenAccount,
  onOpenHistory,
  onOpenPricing,
  onStartTour,
  onOpenCookieSettings = openCookieSettings,
  navigationGroups,
  socialLinks = SITE_SOCIAL_LINKS,
  mobileMenuOpen,
  onMobileMenuOpenChange,
  copy: copyOverrides,
  className = ''
}) => {
  const normalizedLanguage = String(language || 'en').trim().toLowerCase().split(/[-_]/)[0];
  const shellCopy = SHELL_COPY_BY_LANGUAGE[normalizedLanguage] || SHELL_COPY_BY_LANGUAGE.en;
  const copy = { ...shellCopy, ...(copyOverrides || {}) };
  const t = (key, fallback) => (texts && texts[key]) || fallback;
  const headerRef = useRef(null);
  const openIntentTimerRef = useRef(null);
  const closeIntentTimerRef = useRef(null);
  const [openDesktopPanel, setOpenDesktopPanel] = useState('');
  const [revealedDesktopPanels, setRevealedDesktopPanels] = useState(() => new Set());
  const [isScrolled, setIsScrolled] = useState(false);
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  const isMobileControlled = typeof mobileMenuOpen === 'boolean';
  const isMobileOpen = isMobileControlled ? mobileMenuOpen : internalMobileOpen;
  const normalizedZoomLevel = normalizeZoomLevel(zoomLevel);
  const isZoomAtMinimum = normalizedZoomLevel <= MIN_ZOOM_LEVEL;
  const isZoomAtMaximum = normalizedZoomLevel >= MAX_ZOOM_LEVEL;
  const isZoomAtDefault = normalizedZoomLevel === DEFAULT_ZOOM_LEVEL;

  const setMobileOpen = (nextOpen) => {
    if (!isMobileControlled) setInternalMobileOpen(nextOpen);
    onMobileMenuOpenChange?.(nextOpen);
  };

  const localizedHomeHref = localizeInternalHref(homeHref, language);
  const localizedStudioHref = localizeInternalHref(studioHref, language);
  const localizedLoginHref = localizeInternalHref(loginHref, language);
  const localizedAccountHref = localizeInternalHref(accountHref, language);
  const localizedHistoryHref = localizeInternalHref(historyHref, language);
  const localizedPricingHref = localizeInternalHref(pricingHref, language);
  const localizedTourHref = localizeInternalHref(tourHref, language);
  const effectiveBrandHref = isAuthenticated ? localizedStudioHref : localizedHomeHref;
  const effectiveAccountHref = isAuthenticated ? localizedAccountHref : localizedLoginHref;
  const effectiveAccountAction = isAuthenticated ? onOpenAccount : onOpenLogin;
  const effectiveAccountLabel = authLoading
    ? t('loading_text', 'Loading')
    : accountLabel || (isAuthenticated
      ? t('top_workspace', t('sidebar_workspace', copy.account))
      : t('sidebar_login', copy.account));

  const defaultNavigationGroups = useMemo(() => ([
    {
      id: 'product',
      label: copy.product,
      description: t('help_subtitle', 'Metadata, intelligence, exports, and image generation in one workflow.'),
      visual: 'studio',
      previewTitle: t('help_shot_studio_title', 'Studio overview'),
      previewBody: t('help_shot_studio_body', 'Upload assets and start metadata generation.'),
      previewAction: copy.studio,
      items: [
        {
          id: 'metadata', label: t('nav_metadata', 'Metadata Studio'), href: PUBLIC_MARKETING_ROUTES.metadata, icon: Tags,
          description: t('help_feature_keywords_body', 'Generate clean titles, descriptions, and keyword sets fast.')
        },
        {
          id: 'market', label: t('nav_market_intelligence', 'Market Intelligence'), href: PUBLIC_MARKETING_ROUTES.marketIntelligence, icon: CalendarRange,
          description: t('help_mi_body', 'See what is trending and plan content with confidence.')
        },
        {
          id: 'exports', label: copy.platformExports, href: PUBLIC_MARKETING_ANCHORS.platformExports, icon: FileCheck2,
          description: t('help_feature_exports_body', 'CSV downloads and metadata embedding in one workflow.')
        }
      ]
    },
    {
      id: 'solutions',
      label: copy.solutions,
      description: t('help_core_features_body', 'Practical workflows for every stage of a stock portfolio.'),
      visual: 'workflow',
      previewTitle: t('help_quick_start_title', 'Quick start'),
      previewBody: t('help_quick_start_body', 'Move from upload to publish-ready metadata.'),
      items: [
        {
          id: 'batch-portfolios', label: t('help_feature_batch_title', 'Batch processing'), href: PUBLIC_MARKETING_ROUTES.metadata, icon: BarChart3,
          description: t('help_feature_batch_body', 'Handle large folders with parallel processing and smart presets.')
        },
        {
          id: 'png-vector', label: t('help_module_png_vector_title', 'PNG + vector modes'), href: PUBLIC_MARKETING_ROUTES.metadata, icon: Shapes,
          description: t('help_module_png_vector_body', 'Purpose-built modes for transparent assets and illustration workflows.')
        },
        {
          id: 'marketplace-publishing', label: t('help_platforms_title', 'Marketplace presets'), href: PUBLIC_MARKETING_ANCHORS.platformExports, icon: FileCheck2,
          description: t('help_platforms_body', 'Presets tailor metadata to marketplace requirements.')
        },
        {
          id: 'trend-planning', label: t('help_mi_title', 'Market Intelligence'), href: PUBLIC_MARKETING_ROUTES.marketIntelligence, icon: CalendarRange,
          description: t('help_mi_timing', 'Use publishing lead-time guidance to plan before demand peaks.')
        }
      ]
    },
    {
      id: 'tools',
      label: copy.tools,
      description: t('help_core_features_body', 'Focused tools that connect creation, metadata, and delivery.'),
      visual: 'toolkit',
      previewTitle: t('help_core_features_title', 'Core tools'),
      previewBody: t('help_overview_item_2', 'Review, edit, and regenerate with AI tools.'),
      items: [
        {
          id: 'vectorizer', label: t('nav_vectorizer', 'Vectorizer'), href: PUBLIC_MARKETING_ROUTES.vectorizer, icon: Shapes,
          description: t('vectorizer_subtitle', 'Convert raster images into clean SVG vectors.')
        },
        {
          id: 'metadata-tools', label: copy.metadataTools, href: PUBLIC_MARKETING_ANCHORS.coreTools, icon: Settings2,
          description: t('help_module_metadata_controls_body', 'Tune titles, descriptions, keyword rules, and presets.')
        }
      ]
    },
    {
      id: 'resources',
      label: copy.resources,
      description: t('help_title', 'Everything you can do in Stocktag, explained clearly.'),
      visual: 'library',
      previewTitle: t('help_title', 'Learn Stocktag'),
      previewBody: t('help_faq_body', 'Quick answers, guides, and the latest product updates.'),
      items: [
        {
          id: 'blog', label: t('nav_blog', 'Blog'), href: PUBLIC_MARKETING_ROUTES.blog, icon: BookOpen,
          description: t('blog_subtitle', 'Guides and ideas for stronger stock workflows.')
        },
        {
          id: 'help', label: t('nav_help', 'Help'), href: PUBLIC_MARKETING_ROUTES.help, icon: HelpCircle,
          description: t('help_subtitle', 'Guidance for Studio, exports, intelligence, and image generation.')
        },
        {
          id: 'changelog', label: t('nav_changelog', 'Changelog'), href: PUBLIC_MARKETING_ROUTES.changelog, icon: Info,
          description: t('help_changelog_subtitle', 'Product updates, fixes, and improvements.')
        },
        {
          id: 'free-credits', label: t('nav_free_credits', 'Free Credits'), href: PUBLIC_MARKETING_ROUTES.freeCredits, icon: Gift,
          description: resolveReferralCreditsCopy(t('free_credits_subtitle', 'Earn credits and keep creating.'))
        },
        {
          id: 'feedback', label: t('nav_feedback', 'Feedback'), href: PUBLIC_MARKETING_ROUTES.feedback, icon: MessageSquareText,
          description: t('feedback_subtitle', 'Share product ideas and follow their progress.')
        }
      ]
    }
  ]), [copy, texts]);
  const resolvedNavigationGroups = navigationGroups || defaultNavigationGroups;
  const navigationGroupIds = resolvedNavigationGroups.map(getNavigationGroupId);
  const isMegaPanelOpen = navigationGroupIds.includes(openDesktopPanel);

  const legalLinks = [
    { id: 'about', label: copy.about, href: PUBLIC_MARKETING_ROUTES.about, icon: Info },
    { id: 'editorial', label: copy.editorial, href: PUBLIC_MARKETING_ROUTES.editorial, icon: FileCheck2 },
    { id: 'contact', label: copy.contact, href: PUBLIC_MARKETING_ROUTES.contact, icon: Mail },
    { id: 'privacy', label: t('auth_privacy', copy.privacy), href: PUBLIC_MARKETING_ROUTES.privacy, icon: ShieldCheck },
    { id: 'terms', label: t('auth_terms', copy.terms), href: PUBLIC_MARKETING_ROUTES.terms, icon: BookOpen }
  ];

  const clearDesktopPanelTimers = () => {
    window.clearTimeout(openIntentTimerRef.current);
    window.clearTimeout(closeIntentTimerRef.current);
    openIntentTimerRef.current = null;
    closeIntentTimerRef.current = null;
  };

  const openDesktopPanelNow = (panelId) => {
    clearDesktopPanelTimers();
    setOpenDesktopPanel(panelId);
  };

  const queueDesktopPanelOpen = (panelId, delay = 120) => {
    window.clearTimeout(closeIntentTimerRef.current);
    window.clearTimeout(openIntentTimerRef.current);
    closeIntentTimerRef.current = null;
    if (openDesktopPanel === panelId) return;
    openIntentTimerRef.current = window.setTimeout(() => {
      setOpenDesktopPanel(panelId);
      openIntentTimerRef.current = null;
    }, delay);
  };

  const queueDesktopPanelClose = (delay = 180) => {
    window.clearTimeout(openIntentTimerRef.current);
    window.clearTimeout(closeIntentTimerRef.current);
    openIntentTimerRef.current = null;
    closeIntentTimerRef.current = window.setTimeout(() => {
      setOpenDesktopPanel('');
      closeIntentTimerRef.current = null;
    }, delay);
  };

  const focusFirstPanelLink = (panelId) => {
    window.requestAnimationFrame(() => {
      document.getElementById(`pm-nav-panel-${panelId}`)?.querySelector('a, button')?.focus();
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let animationFrame = 0;
    const updateScrollState = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => setIsScrolled(window.scrollY > 10));
    };
    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', updateScrollState);
    };
  }, []);

  useEffect(() => () => clearDesktopPanelTimers(), []);

  useEffect(() => {
    if (!navigationGroupIds.includes(openDesktopPanel)) return;
    setRevealedDesktopPanels((currentPanels) => {
      if (currentPanels.has(openDesktopPanel)) return currentPanels;
      const nextPanels = new Set(currentPanels);
      nextPanels.add(openDesktopPanel);
      return nextPanels;
    });
  }, [openDesktopPanel]);

  useEffect(() => {
    if (!openDesktopPanel) return undefined;

    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        clearDesktopPanelTimers();
        setOpenDesktopPanel('');
      }
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const activePanel = openDesktopPanel;
      clearDesktopPanelTimers();
      setOpenDesktopPanel('');
      headerRef.current
        ?.querySelector(`[data-pm-panel-trigger="${activePanel}"]`)
        ?.focus();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDesktopPanel]);

  useEffect(() => {
    if (!isMobileOpen || typeof document === 'undefined') return undefined;
    const appRoot = document.getElementById('root');
    if (!appRoot) return undefined;

    const previousAriaHidden = appRoot.getAttribute('aria-hidden');
    appRoot.setAttribute('aria-hidden', 'true');

    return () => {
      if (previousAriaHidden === null) appRoot.removeAttribute('aria-hidden');
      else appRoot.setAttribute('aria-hidden', previousAriaHidden);
    };
  }, [isMobileOpen]);

  const closeDesktopPanel = () => {
    clearDesktopPanelTimers();
    setOpenDesktopPanel('');
  };
  const closeMobileMenu = () => setMobileOpen(false);

  const renderZoomControls = (context) => (
    <div className="pm-zoom-control" aria-label={copy.zoom}>
      <button
        type="button"
        onClick={onZoomOut}
        disabled={!onZoomOut || isZoomAtMinimum}
        aria-label={t('top_zoom_out', 'Zoom out')}
        title={t('top_zoom_out', 'Zoom out')}
      >
        <Minus size={15} aria-hidden="true" />
      </button>
      <output aria-live="polite" aria-atomic="true" aria-label={`${copy.zoom}: ${normalizedZoomLevel}%`}>{normalizedZoomLevel}%</output>
      <button
        type="button"
        onClick={onZoomIn}
        disabled={!onZoomIn || isZoomAtMaximum}
        aria-label={t('top_zoom_in', 'Zoom in')}
        title={t('top_zoom_in', 'Zoom in')}
      >
        <Plus size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onZoomReset}
        disabled={!onZoomReset || isZoomAtDefault}
        aria-label={t('top_zoom_reset', 'Reset zoom')}
        title={t('top_zoom_reset', 'Reset zoom')}
      >
        <RotateCcw size={14} aria-hidden="true" />
      </button>
      <span className="pm-sr-only">{context}</span>
    </div>
  );

  const renderThemeControl = () => (
    <div className="pm-theme-control">
      <span>{copy.theme}</span>
      {toggleTheme ? (
        <ThemeToggle
          theme={theme}
          toggleTheme={toggleTheme}
          texts={{
            ...(texts || {}),
            theme_toggle_aria: copy.switchTheme,
            theme_toggle_to_dark: copy.switchTheme,
            theme_toggle_to_light: copy.switchTheme
          }}
        />
      ) : null}
    </div>
  );

  return (
    <header
      ref={headerRef}
      className={`public-marketing-header${isScrolled ? ' is-scrolled' : ''}${isMegaPanelOpen ? ' is-mega-open' : ''} ${className}`.trim()}
      data-public-marketing-header="true"
      data-scroll-state={isScrolled ? 'scrolled' : 'top'}
      data-mega-state={isMegaPanelOpen ? 'open' : 'closed'}
      data-pm-theme={STUDIO_PREVIEW_THEMES.has(theme) ? theme : 'light'}
    >
      <div
        className="pm-header-inner"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) closeDesktopPanel();
        }}
      >
        <a className="pm-brand" href={effectiveBrandHref} aria-label="Stocktag.AI">
          <StocktagBrandLogo variant="full" decorative />
        </a>

        <nav className="pm-desktop-nav" aria-label={t('sidebar_navigation', copy.navigation)}>
          {resolvedNavigationGroups.map((group, groupIndex) => {
            const groupId = getNavigationGroupId(group, groupIndex);
            const isOpen = openDesktopPanel === groupId;
            const shouldRenderVisual = isOpen || revealedDesktopPanels.has(groupId);
            return (
              <div
                className="pm-nav-disclosure"
                key={groupId}
                data-open={isOpen ? 'true' : 'false'}
                onPointerEnter={() => queueDesktopPanelOpen(groupId)}
                onPointerLeave={() => queueDesktopPanelClose()}
                onFocus={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) queueDesktopPanelOpen(groupId);
                }}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) queueDesktopPanelClose(140);
                }}
              >
                <button
                  type="button"
                  className="pm-nav-trigger"
                  data-pm-panel-trigger={groupId}
                  aria-expanded={isOpen}
                  aria-controls={`pm-nav-panel-${groupId}`}
                  onClick={() => openDesktopPanelNow(isOpen ? '' : groupId)}
                  onKeyDown={(event) => {
                    if (event.key !== 'ArrowDown') return;
                    event.preventDefault();
                    openDesktopPanelNow(groupId);
                    focusFirstPanelLink(groupId);
                  }}
                >
                  {group.label}
                  <ChevronDown size={14} aria-hidden="true" />
                </button>
                <div
                  className={`pm-nav-popover pm-mega-panel pm-mega-panel--${group.visual || 'links'}`}
                  id={`pm-nav-panel-${groupId}`}
                  data-state={isOpen ? 'open' : 'closed'}
                  role="region"
                  aria-label={group.label}
                  hidden={!isOpen}
                >
                  <div className="pm-mega-content">
                    <div className="pm-mega-navigation">
                      <div className="pm-mega-intro">
                        <span>{group.label}</span>
                        {group.description ? <p>{group.description}</p> : null}
                      </div>
                      <ul className="pm-mega-link-grid">
                        {(group.items || []).map((item) => {
                          const Icon = item.icon;
                          return (
                            <li key={item.id || item.href}>
                              <ActionLink
                                href={localizeSeoHref(item.href, language)}
                                onAction={item.onAction}
                                onNavigate={closeDesktopPanel}
                              >
                                <span className="pm-mega-link-icon">
                                  {Icon ? <Icon size={18} aria-hidden="true" /> : <ArrowRight size={17} aria-hidden="true" />}
                                </span>
                                <span className="pm-mega-link-copy">
                                  <strong>{item.label}</strong>
                                  {item.description ? <small>{item.description}</small> : null}
                                </span>
                                <ArrowRight className="pm-mega-link-arrow" size={15} aria-hidden="true" />
                              </ActionLink>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    {shouldRenderVisual ? (
                      <MegaPanelVisual
                        group={group}
                        theme={theme}
                        language={language}
                        studioHref={localizedStudioHref}
                        onOpenStudio={onOpenStudio}
                        onNavigate={closeDesktopPanel}
                      />
                    ) : <div className="pm-mega-visual-placeholder" aria-hidden="true" />}
                  </div>
                </div>
              </div>
            );
          })}
          <ActionLink
            className="pm-nav-direct"
            href={localizedPricingHref}
            onAction={onOpenPricing}
            onNavigate={closeDesktopPanel}
            onPointerEnter={() => queueDesktopPanelClose(90)}
          >
            {t('nav_pricing', 'Pricing')}
          </ActionLink>
        </nav>

        <div className="pm-desktop-actions">
          <label className="pm-language-control">
            <Globe2 size={16} aria-hidden="true" />
            <span className="pm-sr-only">{t('languageLabel', 'Language')}</span>
            <select
              name="site-language"
              autoComplete="off"
              value={language || 'en'}
              onChange={(event) => setLanguage?.(event.target.value)}
              disabled={!setLanguage}
              aria-label={t('languageLabel', 'Language')}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>{option.label}</option>
              ))}
            </select>
          </label>

          <div className="pm-display-disclosure">
            <button
              type="button"
              className="pm-icon-button pm-display-trigger"
              data-pm-panel-trigger="display"
              aria-label={copy.displayPreferences}
              title={copy.displayPreferences}
              aria-expanded={openDesktopPanel === 'display'}
              aria-controls="pm-display-panel"
              onClick={() => openDesktopPanelNow(openDesktopPanel === 'display' ? '' : 'display')}
            >
              <MonitorCog size={18} aria-hidden="true" />
            </button>
            <div
              className="pm-display-popover"
              id="pm-display-panel"
              hidden={openDesktopPanel !== 'display'}
            >
              <div className="pm-popover-heading">
                <Settings2 size={17} aria-hidden="true" />
                <strong>{copy.displayPreferences}</strong>
              </div>
              <div className="pm-display-row">
                <span>{copy.zoom}</span>
                {renderZoomControls('desktop')}
              </div>
              <div className="pm-display-row">{renderThemeControl()}</div>
              <ActionLink
                className="pm-popover-action"
                href={localizedTourHref}
                onAction={onStartTour}
                onNavigate={closeDesktopPanel}
              >
                <Info size={16} aria-hidden="true" />
                <span>{t('top_tour', 'Tour')}</span>
              </ActionLink>
              <ActionLink
                className="pm-popover-action"
                href={localizedHistoryHref}
                onAction={onOpenHistory}
                onNavigate={closeDesktopPanel}
              >
                <Clock3 size={16} aria-hidden="true" />
                <span>{t('nav_history', 'History')}</span>
              </ActionLink>
            </div>
          </div>

          <ActionLink
            className="pm-account-link"
            href={effectiveAccountHref}
            onAction={effectiveAccountAction}
            aria-busy={authLoading ? 'true' : undefined}
          >
            <UserRound size={17} aria-hidden="true" />
            <span>{effectiveAccountLabel}</span>
          </ActionLink>
          <ActionLink
            className="pm-studio-cta"
            href={localizedStudioHref}
            onAction={onOpenStudio}
          >
            <Sparkles size={16} aria-hidden="true" />
            <span>{copy.studio}</span>
            <ArrowRight size={15} aria-hidden="true" />
          </ActionLink>
        </div>

        <Dialog.Root open={isMobileOpen} onOpenChange={setMobileOpen}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="pm-mobile-menu-trigger"
              aria-label={copy.menu}
            >
              <Menu size={20} aria-hidden="true" />
              <span>{copy.menu}</span>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="pm-mobile-menu-overlay" />
            <Dialog.Content className="pm-mobile-menu-dialog" aria-modal="true">
              <div className="pm-mobile-menu-header">
                <div>
                  <a className="pm-mobile-brand" href={effectiveBrandHref} onClick={closeMobileMenu} aria-label="Stocktag.AI">
                    <StocktagBrandLogo variant="full" decorative />
                  </a>
                  <Dialog.Title className="pm-mobile-menu-title">{copy.navigation}</Dialog.Title>
                  <Dialog.Description className="pm-mobile-menu-description">
                    {copy.navigationDescription}
                  </Dialog.Description>
                </div>
                <Dialog.Close asChild>
                  <button type="button" className="pm-mobile-close" aria-label={copy.closeMenu}>
                    <X size={20} aria-hidden="true" />
                  </button>
                </Dialog.Close>
              </div>

              <div className="pm-mobile-menu-scroll">
                <ActionLink
                  className="pm-mobile-studio-cta"
                  href={localizedStudioHref}
                  onAction={onOpenStudio}
                  onNavigate={closeMobileMenu}
                >
                  <span><Sparkles size={17} aria-hidden="true" />{copy.studio}</span>
                  <ArrowRight size={17} aria-hidden="true" />
                </ActionLink>

                <div className="pm-mobile-account-grid">
                  <ActionLink
                    href={effectiveAccountHref}
                    onAction={effectiveAccountAction}
                    onNavigate={closeMobileMenu}
                    aria-busy={authLoading ? 'true' : undefined}
                  >
                    <UserRound size={17} aria-hidden="true" />
                    <span>{effectiveAccountLabel}</span>
                  </ActionLink>
                  <ActionLink
                    href={localizedHistoryHref}
                    onAction={onOpenHistory}
                    onNavigate={closeMobileMenu}
                  >
                    <Clock3 size={17} aria-hidden="true" />
                    <span>{t('nav_history', 'History')}</span>
                  </ActionLink>
                  <ActionLink
                    href={localizedPricingHref}
                    onAction={onOpenPricing}
                    onNavigate={closeMobileMenu}
                  >
                    <CreditCard size={17} aria-hidden="true" />
                    <span>{t('nav_pricing', 'Pricing')}</span>
                  </ActionLink>
                  <ActionLink
                    href={localizedTourHref}
                    onAction={onStartTour}
                    onNavigate={closeMobileMenu}
                  >
                    <Info size={17} aria-hidden="true" />
                    <span>{t('top_tour', 'Tour')}</span>
                  </ActionLink>
                </div>

                <nav className="pm-mobile-nav" aria-label={t('sidebar_navigation', copy.navigation)}>
                  <Accordion.Root
                    className="pm-mobile-accordion"
                    type="single"
                    collapsible
                    defaultValue={navigationGroupIds[0]}
                  >
                    {resolvedNavigationGroups.map((group, groupIndex) => {
                      const groupId = getNavigationGroupId(group, groupIndex);
                      const PreviewIcon = group.items?.[0]?.icon || Sparkles;
                      return (
                        <Accordion.Item className="pm-mobile-accordion-item" key={groupId} value={groupId}>
                          <Accordion.Header className="pm-mobile-accordion-heading">
                            <Accordion.Trigger className="pm-mobile-accordion-trigger">
                              <span>{group.label}</span>
                              <ChevronDown size={17} aria-hidden="true" />
                            </Accordion.Trigger>
                          </Accordion.Header>
                          <Accordion.Content className="pm-mobile-accordion-content">
                            <div className="pm-mobile-accordion-content-inner">
                              {group.description ? (
                                <div className={`pm-mobile-group-preview pm-mobile-group-preview--${group.visual || 'links'}`}>
                                  <span aria-hidden="true"><PreviewIcon size={18} /></span>
                                  <p>{group.description}</p>
                                </div>
                              ) : null}
                              <ul>
                                {(group.items || []).map((item) => {
                                  const Icon = item.icon;
                                  return (
                                    <li key={item.id || item.href}>
                                      <ActionLink
                                        href={localizeSeoHref(item.href, language)}
                                        onAction={item.onAction}
                                        onNavigate={closeMobileMenu}
                                      >
                                        <span className="pm-mobile-nav-icon">
                                          {Icon ? <Icon size={18} aria-hidden="true" /> : <ArrowRight size={17} aria-hidden="true" />}
                                        </span>
                                        <span>{item.label}</span>
                                        <ArrowRight size={15} aria-hidden="true" />
                                      </ActionLink>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion.Root>
                </nav>

                <section className="pm-mobile-preferences" aria-labelledby="pm-mobile-preferences-title">
                  <h2 id="pm-mobile-preferences-title">{copy.displayPreferences}</h2>
                  <label className="pm-mobile-language">
                    <span><Globe2 size={16} aria-hidden="true" />{t('languageLabel', 'Language')}</span>
                    <select
                      name="mobile-site-language"
                      autoComplete="off"
                      value={language || 'en'}
                      onChange={(event) => setLanguage?.(event.target.value)}
                      disabled={!setLanguage}
                      aria-label={t('languageLabel', 'Language')}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <div className="pm-mobile-preference-row">
                    <span>{copy.zoom}</span>
                    {renderZoomControls('mobile')}
                  </div>
                  <div className="pm-mobile-preference-row">{renderThemeControl()}</div>
                </section>

                <section className="pm-mobile-legal" aria-labelledby="pm-mobile-legal-title">
                  <h2 id="pm-mobile-legal-title">{copy.legal}</h2>
                  <div className="pm-mobile-legal-links">
                    {legalLinks.map((item) => {
                      const Icon = item.icon;
                      return (
                        <a
                          key={item.id}
                          href={localizeSeoHref(item.href, language)}
                          onClick={closeMobileMenu}
                        >
                          <Icon size={15} aria-hidden="true" />
                          {item.label}
                        </a>
                      );
                    })}
                    <button type="button" onClick={() => {
                      closeMobileMenu();
                      window.setTimeout(() => onOpenCookieSettings?.(), 0);
                    }}>
                      <Settings2 size={15} aria-hidden="true" />
                      {t('cookie_settings', 'Cookie settings')}
                    </button>
                  </div>
                </section>

                {socialLinks?.length ? (
                  <div className="pm-mobile-social" aria-label={copy.social}>
                    {socialLinks.filter((item) => item?.href).map((item) => {
                      const Icon = SOCIAL_ICON_BY_ID[item.id];
                      return (
                        <a
                          key={item.id || item.href}
                          href={item.href}
                          target="_blank"
                          rel="me noopener noreferrer"
                          aria-label={item.label}
                        >
                          {Icon ? <Icon size={17} aria-hidden="true" /> : item.label}
                        </a>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </header>
  );
};

export default PublicMarketingHeader;
